import React, { useState, useEffect } from 'react';
import {
  Reply, Trash2, Star, MoreVertical, Loader,
  ArrowLeft, Download, Eye, EyeOff, Forward,
  Maximize2, Minimize2
} from 'lucide-react';
import './EmailInbox.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function MessageThread({ message, account, onReply, onDelete, onStar, onBack }) {
  const token = localStorage.getItem('crm_token');
  const [fullMsg, setFullMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewHtml, setViewHtml] = useState(true);
  const [showHeaders, setShowHeaders] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!message || !account) return;
    setLoading(true);
    setFullMsg(null);
    fetchBody();
  }, [message?.uid, account?._id]); // eslint-disable-line

  const fetchBody = async () => {
    try {
      const folder = encodeURIComponent(message.folder || 'INBOX');
      const r = await fetch(
        `${API}/email-inbox/accounts/${account._id}/messages/${message.uid}?folder=${folder}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error('Failed to load message');
      const data = await r.json();
      setFullMsg(data);
    } catch (e) {
      console.error('Fetch body error:', e.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleString([], {
      weekday: 'short', year: 'numeric', month: 'short',
      day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Decode quoted-printable encoding (=3D → =, soft line breaks removed)
  const decodeQP = (str) => {
    if (!str) return str;
    return str
      .replace(/=\r?\n/g, '')                                    // soft line break
      .replace(/=([A-F0-9]{2})/gi, (_, hex) =>                   // =3D → =
        String.fromCharCode(parseInt(hex, 16))
      );
  };

  // Prepare body for rendering — decode QP, detect HTML-in-text, sanitize
  const prepareBody = (html, text) => {
    let finalHtml = html || '';
    let finalText = text || '';

    // Decode QP in HTML if it slipped through (=3D guard)
    if (finalHtml.includes('=3D') || /=[A-F0-9]{2}/i.test(finalHtml)) {
      finalHtml = decodeQP(finalHtml);
    }
    // Decode QP in text
    if (finalText.includes('=3D') || /=[A-F0-9]{2}/i.test(finalText)) {
      finalText = decodeQP(finalText);
    }

    // If HTML is empty but text looks like HTML, promote it
    if (!finalHtml && finalText && /<(html|body|div|table|p|span|h[1-6])\b/i.test(finalText)) {
      finalHtml = finalText;
      finalText = finalText
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    return { finalHtml, finalText };
  };

  const sanitizeHtml = (html) => {
    // XSS prevention — strip script tags and inline event handlers
    return html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/\son\w+="[^"]*"/g, '')
      .replace(/\son\w+='[^']*'/g, '')
      .replace(/javascript:/gi, '');
  };


  const msg = fullMsg || message;
  const senderInitial = (msg.fromName || msg.fromEmail || '?')[0].toUpperCase();

  return (
    <div className={`ei-viewer ${isFullscreen ? 'fullscreen' : ''}`}>
      {/* Header */}
      <div className="ei-viewer-header">
        {/* Mobile back button */}
        <button
          onClick={onBack}
          style={{
            background: 'none', border: 'none', color: '#6c63ff',
            cursor: 'pointer', display: 'flex', alignItems: 'center',
            gap: 6, fontSize: 13, marginBottom: 12
          }}
        >
          <ArrowLeft size={16} /> Back to list
        </button>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <h1 className="ei-viewer-subject">{msg.subject || '(no subject)'}</h1>
          <button 
            className="ei-icon-btn" 
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? "Exit full screen" : "Full screen"}
          >
            {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>

        <div className="ei-viewer-meta">
          <div className="ei-viewer-avatar">{senderInitial}</div>
          <div className="ei-viewer-sender-info">
            <div className="ei-viewer-sender-name">{msg.fromName || msg.fromEmail}</div>
            <div className="ei-viewer-sender-email">
              {msg.fromEmail}
              {msg.toAddresses?.length > 0 && (
                <span style={{ marginLeft: 8 }}>→ {msg.toAddresses.join(', ')}</span>
              )}
            </div>
          </div>
          <div className="ei-viewer-date">{formatDate(msg.date)}</div>
        </div>
      </div>

      {/* Action bar */}
      <div className="ei-viewer-actions">
        <button className="ei-btn-secondary" onClick={() => onReply(msg)} style={{ padding: '8px 16px', fontSize: 13 }}>
          <Reply size={15} /> Reply
        </button>
        <button className="ei-btn-secondary" onClick={() => onReply({ ...msg, subject: `Fwd: ${msg.subject}`, fromEmail: '' })} style={{ padding: '8px 16px', fontSize: 13 }}>
          <Forward size={15} /> Forward
        </button>
        <button
          className={`ei-icon-btn ${msg.isStarred ? '' : ''}`}
          onClick={() => onStar(msg)}
          title={msg.isStarred ? 'Unstar' : 'Star'}
          style={{ color: msg.isStarred ? '#f59e0b' : undefined }}
        >
          <Star size={16} fill={msg.isStarred ? 'currentColor' : 'none'} />
        </button>
        <button
          className="ei-icon-btn"
          onClick={() => setViewHtml(!viewHtml)}
          title={viewHtml ? 'View plain text' : 'View HTML'}
        >
          {viewHtml ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
        <button
          className="ei-icon-btn"
          onClick={() => setShowHeaders(!showHeaders)}
          title="Toggle headers"
        >
          <MoreVertical size={16} />
        </button>
        <button
          className="ei-btn-danger"
          onClick={() => onDelete(msg)}
          style={{ padding: '8px 14px', fontSize: 13, marginLeft: 'auto' }}
        >
          <Trash2 size={15} /> Delete
        </button>
      </div>

      {/* Headers detail */}
      {showHeaders && (
        <div style={{
          background: 'rgba(0,0,0,0.2)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          padding: '12px 28px',
          fontSize: 12,
          color: '#666680',
          fontFamily: 'monospace'
        }}>
          <div><strong>Message-ID:</strong> {msg.messageId}</div>
          <div><strong>To:</strong> {msg.toAddresses?.join(', ')}</div>
          {msg.ccAddresses?.length > 0 && <div><strong>Cc:</strong> {msg.ccAddresses.join(', ')}</div>}
          <div><strong>Folder:</strong> {msg.folder}</div>
          <div><strong>UID:</strong> {msg.uid}</div>
        </div>
      )}

      {/* Body */}
      <div className="ei-viewer-body">
        {loading ? (
          <div className="ei-viewer-loading">
            <Loader size={24} className="ei-spinner" />
            Loading message…
          </div>
        ) : (() => {
          const { finalHtml, finalText } = prepareBody(
            fullMsg?.bodyHtml || msg.bodyHtml,
            fullMsg?.bodyText || msg.bodyText
          );
          return (
            <>
              {viewHtml && finalHtml ? (
                <div
                  className="ei-viewer-html"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(finalHtml) }}
                />
              ) : (
                <div className="ei-viewer-text">
                  {finalText || msg.snippet || '(No content)'}
                </div>
              )}


            {/* Attachments */}
            {(fullMsg?.attachments?.length > 0 || msg.hasAttachments) && (
              <div style={{
                marginTop: 24,
                padding: '16px',
                background: 'rgba(108,99,255,0.06)',
                borderRadius: 10,
                border: '1px solid rgba(108,99,255,0.12)'
              }}>
                <div style={{ fontSize: 12, color: '#6c63ff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                  📎 Attachments
                </div>
                {(fullMsg?.attachments || []).map((att, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 12px',
                    background: 'rgba(255,255,255,0.04)',
                    borderRadius: 8,
                    marginBottom: 6,
                    fontSize: 13,
                    color: '#c4c4e0'
                  }}>
                    <span>📄</span>
                    <span style={{ flex: 1 }}>{att.filename}</span>
                    <span style={{ color: '#555570', fontSize: 11 }}>
                      {att.size ? `${(att.size / 1024).toFixed(1)} KB` : ''}
                    </span>
                    <span style={{ color: '#555570', fontSize: 11 }}>{att.contentType}</span>
                  </div>
                ))}
                {!fullMsg && (
                  <p style={{ color: '#555570', fontSize: 13, margin: 0 }}>
                    Attachments listed after full load.
                  </p>
                )}
              </div>
            )}
            </>
          );
        })()}
      </div>
    </div>
  );
}
